import React, { useState } from "react";
import Helmet from "react-helmet";
import { formatNumber, formatCurrency, getDateShort } from "@wrstudios/utils";
import {
  getAverageDOM,
  getAveragePrice,
  getAverageAdjustedPrice,
  getAveragePricePerSqft,
  getAverageAdjustedPricePerSqft,
  hasAveragePriceAdjustments
} from "../../../utils/listings";
import Adjustment from "../../common/Adjustment";
import { Table, TableHeading, TableData } from "../../common/Table";
import { useLive } from "../LiveProvider";
import LiveHeader from "../common/LiveHeader";
import LiveListingDetailsModal from "../listing/LiveListingDetailsModal";
import LiveAveragePricingChart from "./LiveAveragePricingChart";
import {
  Container,
  Group,
  GroupTitle,
  Badge,
  Street,
  CityStateZip,
  TableHeader,
  TableRow,
  TableFooter
} from "./styled/live-average-pricing";

function LiveAveragePricing() {
  const { visibleListingGroups } = useLive();
  const [previewListingId, setPreviewListingId] = useState(null);

  return (
    <>
      <Helmet>
        <title>Average Pricing</title>
      </Helmet>
      <LiveHeader>Average Pricing</LiveHeader>
      <Container>
        {Object.entries(visibleListingGroups).map(([status, listings]) => (
          <LiveGroup
            key={status}
            status={status}
            listings={listings}
            setPreviewListingId={setPreviewListingId}
          />
        ))}
      </Container>
      {previewListingId && (
        <LiveListingDetailsModal
          listingRecordId={previewListingId}
          onClose={() => setPreviewListingId(null)}
        />
      )}
    </>
  );
}

function LiveGroup({ status, listings, setPreviewListingId }) {
  const [hoverListingId, setHoverListingId] = useState(null);
  const averageDOM = getAverageDOM(listings);
  const averagePrice = getAveragePrice(listings);
  const averageAdjustedPrice = getAverageAdjustedPrice(listings);
  const averagePricePerSqft = getAveragePricePerSqft(listings);
  const averageAdjustedPricePerSqft = getAverageAdjustedPricePerSqft(listings);
  const hasPriceAdjustments = hasAveragePriceAdjustments(listings);
  const areAnyListingsSold = listings.some((listing) => listing.soldPrice);

  return (
    <Group key={status}>
      <GroupTitle className="group-title-avg-price">
        <Badge
          style={{
            backgroundColor: (listings[0] || { statusColor: "#000" })
              .statusColor
          }}>
          {listings.length}
        </Badge>
        {status}
      </GroupTitle>
      <LiveAveragePricingChart
        listings={listings}
        hoverListingId={hoverListingId}
        setHoverListingId={setHoverListingId}
        setPreviewListingId={setPreviewListingId}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeading className="table-heading-avg-price">
              Address
            </TableHeading>
            <TableHeading className="table-heading-avg-price">
              {areAnyListingsSold ? "Sold" : "List"} Date
            </TableHeading>
            <TableHeading className="table-heading-avg-price">
              {areAnyListingsSold ? "Sold" : "List"} Price
            </TableHeading>
            {hasPriceAdjustments && (
              <TableHeading className="table-heading-avg-price">
                Adj. Price
              </TableHeading>
            )}
            <TableHeading className="table-heading-avg-price">
              $/Sq.Ft
            </TableHeading>
            {hasPriceAdjustments && (
              <TableHeading className="table-heading-avg-price">
                Adj. $/Sq.Ft
              </TableHeading>
            )}
            <TableHeading className="table-heading-avg-price">Dom</TableHeading>
          </TableRow>
        </TableHeader>
        <tbody>
          {listings.map((listing) => (
            <TableRow
              key={listing.recordId}
              isGhost={listing.isGhost}
              onClick={() => setPreviewListingId(listing.recordId)}
              onMouseEnter={() => setHoverListingId(listing.recordId)}
              onMouseLeave={() => setHoverListingId(null)}>
              <TableData>
                <Street className="live-table-value">
                  {listing.street}{" "}
                  <CityStateZip>{listing.cityStateZip}</CityStateZip>
                </Street>
              </TableData>
              <TableData>
                {areAnyListingsSold ? (
                  <>{listing.soldDate ? getDateShort(listing.soldDate) : "-"}</>
                ) : (
                  <>{listing.listDate ? getDateShort(listing.listDate) : "-"}</>
                )}
              </TableData>
              <TableData>
                {listing.price ? formatCurrency(listing.price) : "-"}
              </TableData>
              {hasPriceAdjustments && (
                <TableData>
                  <Adjustment
                    value={listing.price}
                    adjustedValue={listing.adjustedPrice}
                  />
                </TableData>
              )}
              <TableData>
                {listing.pricePerSqft
                  ? formatCurrency(listing.pricePerSqft)
                  : "-"}
              </TableData>
              {hasPriceAdjustments && (
                <TableData>
                  <Adjustment
                    value={listing.pricePerSqft}
                    adjustedValue={listing.adjustedPricePerSqft}
                  />
                </TableData>
              )}
              <TableData>
                {listing.dom ? formatNumber(listing.dom) : "-"}
              </TableData>
            </TableRow>
          ))}
        </tbody>
        <TableFooter>
          <TableRow>
            <TableData className="live-table-value">Averages</TableData>
            <TableData className="live-table-value">-</TableData>
            <TableData className="live-table-value">
              {averagePrice ? formatCurrency(averagePrice) : "-"}
            </TableData>
            {hasPriceAdjustments && (
              <TableData className="live-table-value">
                <Adjustment
                  value={averagePrice}
                  adjustedValue={averageAdjustedPrice}
                />
              </TableData>
            )}
            <TableData className="live-table-value">
              {averagePricePerSqft ? formatCurrency(averagePricePerSqft) : "-"}
            </TableData>
            {hasPriceAdjustments && (
              <TableData className="live-table-value">
                <Adjustment
                  value={averagePricePerSqft}
                  adjustedValue={averageAdjustedPricePerSqft}
                />
              </TableData>
            )}
            <TableData className="live-table-value">
              {averageDOM ? formatNumber(averageDOM) : "-"}
            </TableData>
          </TableRow>
        </TableFooter>
      </Table>
    </Group>
  );
}

export default LiveAveragePricing;
